import React from 'react';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Youtube } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();

  const quickLinks = [
    { label: 'Home', path: '/' },
    { label: 'Training Programs', path: '/training-programs' },
    { label: 'About Us', path: '/about' },
    { label: 'Validate Certificate', path: '/validate-certificate' },
    { label: 'Contact', path: '/contact' }
  ];

  const trainingCategories = [
    { label: 'Quality Management',         path: '/training/iso-training-services' },
    { label: 'Leadership & Management',    path: '/training/leadership-and-management-training' },
    { label: 'Communication Skills',       path: '/training/communication-skills-training-chennai' },
    { label: 'Soft Skills Training',       path: '/training/soft-skills-training-chennai' },
    { label: 'Faculty Development',        path: '/training/faculty-development-program' },
    { label: 'Industry-Specific Training', path: '/industry-training' },
  ];

  return (
    <footer className="bg-gray-900 text-gray-300">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* About Section */}
          <div>
            <div className="flex items-start gap-3 mb-4">
              {/* Logo with white background for visibility */}
              <div className="bg-white p-2 rounded-lg shadow-md flex-shrink-0">
                <img 
                  src="https://customer-assets.emergentagent.com/job_training-hub-107/artifacts/shpkz69x_400%20x%20400%20logo-16.png" 
                  alt="ISDE Academy Logo" 
                  className="h-14 w-auto"
                />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-1">
                  ISDE <span className="text-blue-400">Academy</span>
                </h3>
                <p className="text-sm text-gray-400">Training Excellence Since 2015</p>
              </div>
            </div>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Providing top quality training to Manufacturing, IT sector, and educational institutions. Building excellence through education.
            </p>
            <div className="flex gap-4">
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-all hover:scale-110"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-all hover:scale-110"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-all hover:scale-110"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-blue-600 transition-all hover:scale-110"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-0 h-0.5 bg-blue-400 group-hover:w-4 transition-all"></span>
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Training Categories */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Training Categories</h4>
            <ul className="space-y-3">
              {trainingCategories.map((category) => (
                <li key={category.label}>
                  <button
                    onClick={() => navigate(category.path)}
                    className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-2 group"
                  >
                    <span className="w-0 h-0.5 bg-blue-400 group-hover:w-4 transition-all"></span>
                    {category.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold text-white mb-4">Contact Us</h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Phone</p>
                  <a href="tel:04449591335" className="text-gray-300 hover:text-blue-400 transition-colors">
                    044 49591335
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Email</p>
                  <a href="mailto:training@isde.academy" className="text-gray-300 hover:text-blue-400 transition-colors break-all">
                    training@isde.academy
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-blue-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-500 mb-1">Location</p>
                  <p className="text-gray-300">Chennai, Tamil Nadu, India</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} ISDE Academy. All Rights Reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="text-gray-500 hover:text-blue-400 transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-400 transition-colors">
                Terms of Service
              </a>
              <a href="#" className="text-gray-500 hover:text-blue-400 transition-colors">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
